import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig, type AxiosProgressEvent } from 'axios';
import { getToken, getDivisionId, useAuthStore, type NavPermissions } from '../auth/store';
import { loadingBegin, loadingEnd, setLoadingProgress, useLoadingStore } from '../components/loading';
import { errorDlg } from '../components/confirm/dialogBus';
import { buildNavActionMap } from '../layouts/navPermissions';

/** Per-request loading bookkeeping. Pass `{ silentLoading: true }` on a call to
 *  keep it OUT of the global loader (background polls: app-config, activity). */
interface LoadingRequestConfig extends InternalAxiosRequestConfig {
  silentLoading?: boolean;
  _loadingId?: number;
}

/** Drive the determinate progress bar from REAL transfer bytes (so the bar's
 *  speed reflects the actual data / download time, §18.6).  Only when the total
 *  size is known AND this is the sole in-flight op — otherwise stay indeterminate
 *  (concurrent transfers can't share one bar). */
function reportTransfer(e: AxiosProgressEvent): void {
  const total = e.total ?? 0;
  if (total > 0 && useLoadingStore.getState().active <= 1) {
    setLoadingProgress(Math.round((e.loaded / total) * 100));
  }
}

/**
 * Single Axios instance. Attaches the JWT bearer token and the
 * X-Division-Id header on every request (CLAUDE.md Section 7.3).
 */
export const api = axios.create({
  // In the production build the API serves this SPA from the SAME origin, so use
  // relative URLs ('') — the page then calls whatever host/port served it
  // (:5030, alpacalims.com, etc.) with no hardcoding. Only the Vite dev server
  // (separate :3000) needs the explicit :5020 API address. An explicit
  // VITE_API_BASE_URL always wins when set.
  baseURL: import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? 'http://localhost:5020' : ''),
});

// ── Audit "reason for change" (standing rule 11 / ALPACA ReasonRequired) ──────
// When a form has ReasonRequired ON (Settings → Audit Trail Configuration), the
// caller prompts for a reason (useReasonGate) and ARMS it here; the request
// interceptor then attaches it as the X-Audit-Reason header on every mutating
// request until disarmed.  The API's AuditTrailService writes it onto the audit
// row with no per-handler wiring.  URL-encoded so non-ASCII reasons are header-safe.
let _auditReason: string | null = null;

/** Arm the reason for change for subsequent mutating requests. */
export function armAuditReason(reason: string | null | undefined): void {
  const r = (reason ?? '').trim();
  _auditReason = r.length ? r : null;
}
/** Clear the armed reason. */
export function disarmAuditReason(): void { _auditReason = null; }
/** Run `fn` with `reason` armed (so the mutations it fires carry X-Audit-Reason),
 *  always disarming afterwards.  An empty/whitespace reason arms nothing. */
export async function runWithAuditReason<T>(
  reason: string | null | undefined, fn: () => Promise<T>,
): Promise<T> {
  armAuditReason(reason);
  try { return await fn(); } finally { disarmAuditReason(); }
}

api.interceptors.request.use((cfg) => {
  const token = getToken();
  const divisionId = getDivisionId();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  if (divisionId) cfg.headers['X-Division-Id'] = divisionId;
  // Attach the armed audit reason on mutating requests only (POST/PUT/PATCH/DELETE).
  if (_auditReason) {
    const m = (cfg.method ?? 'get').toLowerCase();
    if (m === 'post' || m === 'put' || m === 'patch' || m === 'delete') {
      cfg.headers['X-Audit-Reason'] = encodeURIComponent(_auditReason);
    }
  }
  // Global loader (§18.6): begin one tracked op per request automatically, so
  // EVERY API call drives the shared loader with no per-call code.  Callers can
  // opt out with { silentLoading: true } for background polls that shouldn't
  // flash the indicator.
  const lc = cfg as LoadingRequestConfig;
  if (!lc.silentLoading) {
    lc._loadingId = loadingBegin();
    // Real transfer progress — bar speed follows the actual data (downloads,
    // uploads).  Respect a caller's own handler if it set one.
    if (!lc.onDownloadProgress) lc.onDownloadProgress = reportTransfer;
    if (!lc.onUploadProgress) lc.onUploadProgress = reportTransfer;
  }
  return cfg;
});

// ─── Silent re-login on 401 ──────────────────────────────────────────────
// "Keep me signed in" = Chrome-style: the user expects ZERO interruption.
// When the JWT expires (24-hour server-side cap), instead of bouncing to
// /login we silently re-POST the saved username/password, mint a fresh
// JWT, and replay the original request.  The user sees nothing.
//
// Limitations:
//  • Only works for users WITHOUT 2FA/MFA — those still see the OTP step
//    (no way to silently solve a one-time code).
//  • Only works when the user previously checked "Keep me signed in" so
//    the creds are in `alpaca.login.remembered`.
//  • If silent re-login fails (wrong creds, server down, 2FA challenge),
//    we fall back to the original behaviour: clear the session and let
//    ProtectedRoute redirect to /login (form pre-filled from the same
//    saved creds, just one click to sign in).
//
// In-flight de-dup: a single Promise is shared across concurrent 401s so
// 20 simultaneous failing requests trigger ONE re-login, not 20.
// ─────────────────────────────────────────────────────────────────────────
interface RememberedCreds { username: string; password: string }

function readRemembered(): RememberedCreds | null {
  try {
    const raw = localStorage.getItem('alpaca.login.remembered');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RememberedCreds;
    return parsed.username && parsed.password ? parsed : null;
  } catch { return null; }
}

interface ServerPerms {
  isAdmin?: boolean;
  roles?: string;
  allowedNavViews?: Array<{ navigationView?: string; navigationCaption?: string }>;
  allowedDashboards?: Array<{ dashboardId?: string }>;
  formVisibility?: Record<string, boolean>;
  dataReviewNav?: {
    sdmsResultValidation: boolean;
    sdmsResultApproval: boolean;
    resultValidation: boolean;
    resultApproval: boolean;
    fieldDataValidation: boolean;
    fieldDataApproval: boolean;
  };
  avaEnabled?: boolean;
  navActions?: Array<{
    navigationView?: string | null;
    canRead?: boolean; canWrite?: boolean; canCreate?: boolean; canDelete?: boolean;
  }>;
}
interface ServerLoginEnvelope {
  success: boolean;
  data?: {
    requires?: 'none' | '2fa' | 'mfa';
    auth?: { token: string; user: { id: string; name: string; email: string } };
    permissions?: ServerPerms;
  };
}

function toStorePerms(p: ServerPerms | undefined): NavPermissions | null {
  if (!p) return null;
  return {
    isAdmin: p.isAdmin ?? false,
    roles:   p.roles   ?? '',
    allowedViews:      (p.allowedNavViews ?? []).map(r => r.navigationView ?? '').filter(Boolean),
    allowedDashboards: (p.allowedDashboards ?? []).map(d => (d.dashboardId ?? '').toLowerCase()).filter(Boolean),
    allowedCaptions:   (p.allowedNavViews ?? []).map(r => r.navigationCaption ?? '').filter(Boolean),
    formVisibility:    p.formVisibility ?? {},
    dataReviewNav:     p.dataReviewNav,
    avaEnabled:        p.avaEnabled,
    navActions:        buildNavActionMap(p.navActions),
  };
}

let reloginInFlight: Promise<boolean> | null = null;

async function silentRelogin(): Promise<boolean> {
  if (reloginInFlight) return reloginInFlight;
  reloginInFlight = (async () => {
    const creds = readRemembered();
    if (!creds) return false;
    // Who is this tab signed in as?  The saved credentials are shared by every
    // tab of the browser profile, so if another tab signed in as someone else
    // they now belong to THAT person.  Replaying them here would silently turn
    // this tab into a different user — and every record saved afterwards would
    // be attributed to them (audit, CreatedBy, ValidatedBy, e-signatures).
    // Capture the current identity so we can refuse that below.
    const priorUserId = useAuthStore.getState().user?.id ?? null;
    try {
      // Raw axios call — bypass the `api` instance to avoid the interceptor
      // loop, and don't carry the dead Authorization header.
      const res = await axios.post<ServerLoginEnvelope>(
        `${api.defaults.baseURL ?? ''}/api/v1/auth/login`,
        { username: creds.username, password: creds.password, rememberMe: true },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const data = res.data?.data;
      // 2FA / MFA users: server returns a challenge instead of a token.
      // Can't silently solve a one-time code — fall through to /login.
      if (!data?.auth?.token) return false;
      // IDENTITY GUARD: never silently become somebody else.  If the saved
      // credentials now belong to a different user, drop the session and let
      // ProtectedRoute send this tab to the login page instead.
      if (priorUserId && data.auth.user?.id && data.auth.user.id !== priorUserId) {
        useAuthStore.getState().expireSession();
        return false;
      }
      useAuthStore.getState().signIn(
        data.auth.token,
        data.auth.user,
        true,
        toStorePerms(data.permissions),
      );
      return true;
    } catch {
      return false;
    } finally {
      reloginInFlight = null;
    }
  })();
  return reloginInFlight;
}

interface RetriableConfig extends AxiosRequestConfig { _retried?: boolean }

api.interceptors.response.use(
  (res) => {
    // Global loader: end the op for this completed request.
    const id = (res.config as LoadingRequestConfig)._loadingId;
    if (id != null) loadingEnd(id);
    return res;
  },
  async (err) => {
    const cfg = err.config as (RetriableConfig & LoadingRequestConfig) | undefined;
    const status = err.response?.status as number | undefined;
    const url = (cfg?.url ?? '') as string;

    // Global loader: end the op for the failed request (a silent-relogin retry
    // re-enters the request interceptor and starts its own).
    if (cfg?._loadingId != null) {
      loadingEnd(cfg._loadingId);
      cfg._loadingId = undefined;
    }

    // Concurrent-login: this session is no longer live (it timed out after a
    // long idle period, or an administrator ended it). Do NOT silent-relogin —
    // that would silently consume a licence again. Sign out cleanly and say why.
    const errCode = (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (status === 401 && errCode === 'SESSION_EVICTED') {
      useAuthStore.getState().expireSession();
      try {
        void errorDlg('Your session has ended because it was inactive for too long. Please sign in again.');
      } catch { /* dialog provider not mounted — ProtectedRoute still redirects to /login */ }
      return Promise.reject(err);
    }

    // Only intercept 401s on already-authenticated requests, and never on
    // the login endpoint itself (would loop forever on bad credentials).
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/verify');
    if (status === 401 && getToken() && cfg && !cfg._retried && !isAuthEndpoint) {
      cfg._retried = true;
      const ok = await silentRelogin();
      if (ok) {
        // Replay the original request with the fresh token (request
        // interceptor will pick the new token from the store).
        return api.request(cfg);
      }
      // Silent re-login failed (no saved creds / wrong creds / 2FA / server
      // down) — clear the JWT but KEEP the saved creds, so the user lands
      // on /login with the form pre-filled and signs in with one click.
      useAuthStore.getState().expireSession();
    }
    return Promise.reject(err);
  },
);
